import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconLockCloseFilled } from '@iwoca/icons';
import {
  Button,
  Card,
  Input,
  Select,
  CheckBox,
  Message,
  Modal,
} from '@iwoca/orion';
import clsx from 'clsx';
import { useAmplitude } from 'hooks/useAmplitude';
import { useInvalidateAuth } from 'hooks/useAuth';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateUKAddress } from 'utils/addressBuilder';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  DUPLICATE_EMAIL_MESSAGE_UK,
  getDuplicateEmailHandlers,
} from '../duplicateEmail.helpers';
import {
  TIntroducerType,
  TRegistrationFormDataUK,
} from '../registration.types';
import styles from '../RegistrationForm.module.css';
import { personalInformationSchemaUK } from '../registrationSchema';
import { submitReferralRegistrationForm } from './ReferralRegistrationForm.helpers';

export const ReferralRegistrationForm = () => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAmplitude();
  const inviteId = searchParams.get('introducer') as string | undefined;
  const introducerTypeFromQueryParameter: TIntroducerType =
    searchParams.get('introducerType') === 'accountant'
      ? 'accountant'
      : 'broker';
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();
  const invalidateAuth = useInvalidateAuth();

  const {
    register,
    handleSubmit,
    trigger,
    setError,
    formState: { errors },
  } = useForm<TRegistrationFormDataUK>({
    defaultValues: {
      introducerType: introducerTypeFromQueryParameter,
    },
    resolver: yupResolver(personalInformationSchemaUK),
  });

  const { onEmailBlur, onDuplicateEmail } = getDuplicateEmailHandlers({
    trigger,
    setError,
    message: DUPLICATE_EMAIL_MESSAGE_UK,
  });

  const onSubmit = async (values: TRegistrationFormDataUK) => {
    setIsLoading(true);

    const handleSuccess = async () => {
      trackEvent(AMPLITUDE_EVENTS.introducerRegisterSubmitted, {
        'Introducer type': values.introducerType,
        'Invite link': inviteId,
      });

      // Refresh auth before redirecting; otherwise ProductsLoader sees
      // the cached anonymous result and bounces the new user to /403.
      await invalidateAuth();
      navigate(PATH_NAMES.home());
    };

    const registeredTradingAddress = generateUKAddress(values);

    await submitReferralRegistrationForm({
      values: { ...values, registeredTradingAddress, uuid: inviteId },
      onSuccess: handleSuccess,
      onError: () => {
        setShowErrorModal(true);
      },
      onDuplicateEmail,
    });

    setIsLoading(false);
  };

  const renderAccountInfo = () => {
    return (
      <>
        <h2>Account Information</h2>
        <Card className={styles.accountInformationContainer}>
          <div className={styles.formField}>
            <Select
              {...register('title')}
              label="Title"
              id="title"
              name="title"
              type="text"
            >
              <option value="Mr">Mr</option>
              <option value="Ms">Ms</option>
              <option value="Mrs">Mrs</option>
              <option value="Miss">Miss</option>
            </Select>
          </div>
          <>
            <div className={clsx(styles.formField, styles.firstName)}>
              <Input
                {...register('firstName')}
                id="firstName"
                name="firstName"
                type="text"
                label="First Name"
                placeholder="John"
                error={errors.firstName && errors.firstName.message}
              />
            </div>
            <div className={clsx(styles.formField, styles.lastName)}>
              <Input
                {...register('lastName')}
                id="lastName"
                name="lastName"
                type="text"
                label="Last Name"
                placeholder="Smith"
                error={errors.lastName && errors.lastName.message}
              />
            </div>
          </>

          <div className={clsx(styles.formField, styles.email)}>
            <Input
              {...register('email', { onBlur: onEmailBlur })}
              id="email"
              name="email"
              type="text"
              label="Email address"
              placeholder="me@iwoca.co.uk"
              error={errors.email && errors.email.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('phoneNumber')}
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              label="Phone Number"
              placeholder="+44 123456789"
              error={errors.phoneNumber && errors.phoneNumber.message}
            />
          </div>
        </Card>
      </>
    );
  };

  return (
    <div className={clsx(styles.container, styles.ukForm)}>
      <h1>Sign up to create an introducer account</h1>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {renderAccountInfo()}
        <div className={styles.privacyPolicyAgreement}>
          <CheckBox
            {...register('privacyPolicyAgreement')}
            id="privacyPolicyAgreement"
            name="privacyPolicyAgreement"
            data-testid="privacy-policy-agreement"
          />
          <div className={styles.privacyPolicyDescription}>
            <p>
              <strong>
                I agree to iwoca carrying out due diligence on the company I
                represent and understand that iwoca will contact me for
                signature on the iwoca introducer agreement.
              </strong>
            </p>
            <p>
              Your privacy is important to us at iwoca, and we’re committed to
              protecting your personal data. We’ll store your personal data
              securely, and we’ll only get in touch in line with our{' '}
              <a
                href="https://www.iwoca.co.uk/introducer-privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>{' '}
              or if you opt in for extra communication from us.
            </p>
            {errors.privacyPolicyAgreement && (
              <Message
                message={errors.privacyPolicyAgreement.message || ''}
                type="error"
              />
            )}
          </div>
        </div>
        <div className={styles.footerItem}>
          <p className={styles.submitDescription}>
            We’ll process your registration.
          </p>
        </div>
        <div className={styles.footerItem}>
          <Button
            variant="primary"
            size="small"
            type="submit"
            loading={isLoading}
            className={styles.submitButton}
          >
            Create an account
            <IconLockCloseFilled />
          </Button>
        </div>
      </form>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="There's a problem with the info you've entered."
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          If you have an account with iwoca, use the login button in the top
          right, otherwise give us a call and we’ll help you get logged in.
        </p>
      </Modal>
    </div>
  );
};
